import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Row, Col, ListGroup, Image, Card, Button } from "react-bootstrap";
import Loading from "../components/Loading";
import Message from "../components/Message";
import { useSelector, useDispatch } from "react-redux";
import {
  getOrderDetails,
  payOrder,
  deliverOrder,
} from "../redux/actions/orderActions";

const OrderScreen = () => {
  let params = useParams();
  let navigate = useNavigate();
  const dispatch = useDispatch();

  const { order, loading, error } = useSelector((state) => state.orderDetails);

  // 🧪 DEBUG - Add this temporarily
console.log("🛒 Order Items Debug:", order?.orderItems?.map(item => ({
  name: item.name,
  finalLineTotal: item.finalLineTotal,
  discountedPrice: item.discountedPrice,
  qty: item.qty,
  categoryDiscountPercent: item.categoryDiscountPercent,
  price: item.price
})));
console.log("💰 Order Totals:", {
  itemsPrice: order?.itemsPrice,
  couponDiscount: order?.couponDiscount,
  totalPrice: order?.totalPrice
});


  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const { loading: loadingPay, success: successPay } = useSelector(
    (state) => state.orderPay
  );

  const {
    loading: loadingDeliverd,
    error: errorDeliverd,
    success: successDeliverd,
  } = useSelector((state) => state.orderDeliver);

  useEffect(() => {
    if (!order || successPay) {
      dispatch(getOrderDetails(params.id));
    }
    dispatch(getOrderDetails(params.id));
  }, [dispatch, successPay, params.id, successDeliverd]);

  const successPaymentHandler = (paymentResult) => {
    dispatch(payOrder(params.id, paymentResult));
  };

  const deliverHandler = () => {
    dispatch(deliverOrder(params.id));
  };

  const getFinalLineTotal = (item, order) => {
  // Use saved value if exists
  if (item.finalLineTotal !== undefined) {
    return Math.round(item.finalLineTotal);
  }
  
  // Emergency recalculation (exact PlaceOrderScreen logic)
  const categoryPrice = item.discountedPrice || item.price || 0;
  const categoryTotal = categoryPrice * (item.qty || 1);
  
  if (!order.couponDiscount || order.couponDiscount === 0) {
    return Math.round(categoryTotal);
  }
  
  // Proportional coupon discount
  const totalItemsAfterCategory = order.orderItems.reduce((sum, i) => {
    return sum + ((i.discountedPrice || i.price || 0) * (i.qty || 1));
  }, 0);
  
  const lineShare = categoryTotal / totalItemsAfterCategory;
  const couponDiscountForLine = lineShare * order.couponDiscount;
  
  return Math.round(categoryTotal - couponDiscountForLine);
};



  return (
    <>
      {loading ? (
        <Loading />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : (
        <>
          <h1>Order #{order._id}</h1>
          <Row>
            <Col md={8}>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <h4>Shipping</h4>
                  <p>
                    <strong>Name: </strong>
                    {order.user.name}
                    <br />
                    <strong>Email: </strong>
                    <a href={`mailto:${order.user.email}`}>
                      {order.user.email}
                    </a>
                  </p>
                  <strong>Address:</strong>
                  <p>
                    {order.shippingAddress.address},{" "}
                    {order.shippingAddress.city}{" "}
                    {order.shippingAddress.postalCode},{" "}
                    {order.shippingAddress.country}
                  </p>
                  <strong>Receiver&apos;s Contact Number:</strong>
                  <p>{order.shippingAddress.receiverContactNumber}</p>
                  {order.isDelivered ? (
                    <Message variant="success">
                      Delivered on {order.deliveredAt.substring(0, 10)}
                    </Message>
                  ) : (
                    <Message variant="danger">Not Delivered</Message>
                  )}
                </ListGroup.Item>

                <ListGroup.Item>
                  <h4>Payment Method</h4>
                  <p>
                    <strong>Method : </strong>
                    {order.paymentMethod}
                  </p>
                  {order.isPaid ? (
                    <Message variant="success">
                      Paid on {order.paidAt.substring(0, 10)}
                    </Message>
                  ) : order.paymentMethod === "UPI" ? (
                    <Message variant="info">
                      Payment verification will be completed soon.
                    </Message>
                  ) : (
                    <Message variant="danger">Not Paid</Message>
                  )}
                </ListGroup.Item>

                <ListGroup.Item>
                  <h4>Order Items</h4>
                  {order.orderItems.length === 0 ? (
                    <Message variant="info">Your cart is empty</Message>
                  ) : (
                    <>
                      {order.orderItems.map((item, index) => (
                        <ListGroup.Item key={index}>
                          {/* Horizontal row: small image, details, price */}
                          <div className="d-flex align-items-center">
                            {/* Left: image */}
                            <div
                              style={{
                                flex: "0 0 60px",
                                marginRight: "10px",
                              }}
                            >
                              <Image
                                src={item.image}
                                alt={item.name}
                                rounded
                                style={{
                                  width: "100%",
                                  aspectRatio: "2 / 3",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            </div>

                            {/* Middle: name + size + qty */}
                            <div className="flex-grow-1">
                              <Link to={`/product/${item.product}`}>
                                <div
                                  style={{
                                    marginBottom: "2px",
                                    fontSize: "14px",
                                    color: "#2b2b2b",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {item.name}
                                </div>
                              </Link>

                              {item.size && (
                                <div
                                  style={{
                                    fontSize: "14px",
                                    color: "#555",
                                  }}
                                >
                                  Size: {item.size}
                                </div>
                              )}

                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#4b4b4bff",
                                }}
                              >
                                Qty: {item.qty}
                              </div>
                            </div>

                            {/* Right: price / discount */}
                            <div
                              className="text-end"
                              style={{
                                minWidth: "90px",
                                marginLeft: "8px",
                                fontSize: "14px",
                              }}
                            >
                              {item.categoryDiscountPercent ? (
                                <>
                                  <div
                                    style={{
                                      color: "#3d3d3dff",
                                      textDecoration: "line-through",
                                    }}
                                  >
                                    {item.qty} x ₹
                                    {Math.round(item.originalPrice)}
                                  </div>
                                  <div
                                    style={{
                                      color: "#198754",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {item.qty} x ₹
                                    {Math.round(item.discountedPrice)}
                                  </div>
                                  <span className="badge bg-success mt-1">
                                    {item.categoryDiscountPercent}% OFF
                                  </span>
                                  <div style={{ marginTop: "2px" }}>
                                    = ₹{getFinalLineTotal(item, order)}
                                  </div>
                                </>
                              ) : (
                                <div>
                                  {item.qty} x ₹{Math.round(item.price)} = ₹
                                  {item.qty * Math.round(item.price)}
                                </div>
                              )}
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </>
                  )}
                </ListGroup.Item>
              </ListGroup>
            </Col>

            <Col md={4}>
              <Card className="no-hover-card">
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <h4>Order Summary</h4>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row>
                      <Col>Items</Col>
                      <Col className="text-end">
                        Rs.{" "}
                        {order.itemsPrice ||
                          (order.totalPrice - order.shippingPrice).toFixed(2)}
                      </Col>
                    </Row>
                  </ListGroup.Item>

                  {order.couponDiscount && order.couponDiscount > 0 && (
                    <ListGroup.Item>
                      <Row>
                        <Col>Coupon ({order.couponCode})</Col>
                        <Col className="text-end" style={{ color: "#198754" }}>
                          - Rs. {order.couponDiscount.toFixed(2)}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  )}

                  <ListGroup.Item>
                    <Row>
                      <Col>Shipping</Col>
                      <Col className="text-end">Rs. {order.shippingPrice}</Col>
                    </Row>
                  </ListGroup.Item>

                  <ListGroup.Item>
                    <Row>
                      <Col>Total</Col>
                      <Col className="text-end">Rs. {order.totalPrice}</Col>
                    </Row>

                    {/* NEW: per-item amounts after discounts */}
                    <div style={{ marginTop: "8px" }}>
                      <div
                        style={{
                          fontWeight: "500",
                          marginBottom: "6px",
                          fontSize: "0.9rem",
                        }}
                      >
                        Items in this order
                      </div>
                      {order.orderItems.map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "0.9rem",
                            marginBottom: 4,
                          }}
                        >
                          <span>
                            {item.name} × {item.qty}
                          </span>
<span>Rs. {getFinalLineTotal(item, order)}</span>
                        </div>
                      ))}
                    </div>

                    {/* Extra content under itemized list */}
                    <div style={{ marginTop: "8px" }}>
                      {!order.isPaid && (
                        <>
                          {order.paymentMethod === "UPI" ? (
                            <Message variant="info">
                              Your payment is pending verification. You’ll be
                              notified once it’s confirmed.
                            </Message>
                          ) : order.paymentMethod === "COD" ? (
                            <Message variant="success">
                              Order placed successfully! We will contact you
                              soon for confirmation.
                            </Message>
                          ) : (
                            <Message variant="info">
                              Please complete your payment.
                            </Message>
                          )}
                        </>
                      )}

                      {loadingDeliverd ? (
                        <Loading />
                      ) : (
                        userInfo.isAdmin &&
                        order.isPaid &&
                        !order.isDelivered && (
                          <Button
                            onClick={deliverHandler}
                            className="w-100"
                            variant="primary"
                          >
                            Mark as delivered
                          </Button>
                        )
                      )}

                      {errorDeliverd && (
                        <Message variant="danger">{errorDeliverd}</Message>
                      )}
                    </div>
                  </ListGroup.Item>
                </ListGroup>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default OrderScreen;
